import { useState, useEffect } from 'react'
import { Bell, X } from 'lucide-react'
import { useStore } from '../store/useStore'
import { 
  isPushSupported, 
  requestNotificationPermission, 
  subscribeToPush,
  getNotificationPermission,
  isPWA
} from '../utils/pushNotifications'

const STORAGE_KEY = 'p5ai_notification_prompt_dismissed'
const PROMPT_DELAY = 2000 // Show 2 seconds after first prompt

export default function NotificationPrompt() {
  const hasSubmittedFirstPrompt = useStore(state => state.hasSubmittedFirstPrompt)
  const [show, setShow] = useState(false)
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState(null) // 'success', 'denied', 'error'

  useEffect(() => {
    // Only show after user has submitted their first prompt
    if (!hasSubmittedFirstPrompt) return
    
    // Check if we should show the prompt
    const checkShouldShow = () => {
      // Check if iOS - always show to educate about "Add to Home Screen"
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
      
      // On non-iOS, don't show if not supported
      if (!isIOS && !isPushSupported()) return false
      
      // Don't show if already granted or denied (and push is supported)
      if (isPushSupported()) {
        const permission = getNotificationPermission()
        if (permission === 'granted' || permission === 'denied') return false
      }
      
      // Don't show if user dismissed before
      let dismissed = null
      try {
        dismissed = localStorage.getItem(STORAGE_KEY)
      } catch (e) {
        dismissed = null
      }
      if (dismissed) return false
      
      return true
    }

    if (checkShouldShow()) {
      // Show after delay (gives time for AI to start responding)
      const timer = setTimeout(() => setShow(true), PROMPT_DELAY)
      return () => clearTimeout(timer)
    }
  }, [hasSubmittedFirstPrompt])

  const handleEnable = async () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    const isInstalled = isPWA()
    
    // On iOS Safari (not PWA), show instructions instead
    if (isIOS && !isInstalled) {
      setStatus('ios-instructions')
      return
    }
    
    // Check if push is actually supported
    if (!isPushSupported()) {
      setStatus('unsupported')
      return
    }
    
    setLoading(true)
    
    try {
      // Request permission first
      const permResult = await requestNotificationPermission()
      
      if (!permResult.success) {
        if (permResult.reason === 'denied') {
          setStatus('denied')
        } else {
          setStatus('error')
        }
        setLoading(false)
        return
      }
      
      // Subscribe to push
      let deviceId = null
      try {
        deviceId = localStorage.getItem('p5ai_device_id')
      } catch (e) {
        deviceId = null
      }
      if (!deviceId) {
        deviceId = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : String(Date.now())
      }
      try {
        localStorage.setItem('p5ai_device_id', deviceId)
      } catch (e) {
      }
      
      const subResult = await subscribeToPush(deviceId)
      
      if (subResult.success) {
        setStatus('success')
        setTimeout(() => setShow(false), 2000)
      } else {
        setStatus('error')
      }
    } catch (error) {
      console.error('[Notification] Enable failed:', error)
      setStatus('error')
    }
    
    setLoading(false)
  }

  const handleDismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'true')
    } catch (e) {
    }
    setShow(false)
  }

  if (!show) return null

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
  const isInstalled = isPWA()

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1e1e2e] rounded-xl shadow-2xl max-w-sm w-full p-6 relative border border-white/10">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-white/50 hover:text-white"
        >
          <X size={20} />
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
            <Bell size={32} className="text-white" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-xl font-bold text-white text-center mb-2">
          Enable Notifications?
        </h2>

        {/* Description */}
        <p className="text-white/70 text-center text-sm mb-4">
          Get notified when your AI response is ready, even if you close the app or switch tabs.
        </p>

        {/* iOS Warning */}
        {isIOS && !isInstalled && (
          <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-3 mb-4">
            <p className="text-yellow-300 text-xs text-center">
              📱 <strong>iPhone/iPad:</strong> First tap Share → "Add to Home Screen", then open from there to enable notifications.
            </p>
          </div>
        )}

        {/* Status Messages */}
        {status === 'success' && (
          <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-3 mb-4">
            <p className="text-green-300 text-sm text-center">
              ✅ Notifications enabled!
            </p>
          </div>
        )}
        
        {status === 'denied' && (
          <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 mb-4">
            <p className="text-red-300 text-xs text-center">
              Notifications blocked. Enable in browser settings.
            </p>
          </div>
        )}
        
        {status === 'error' && (
          <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 mb-4">
            <p className="text-red-300 text-xs text-center">
              Something went wrong. Try again later.
            </p>
          </div>
        )}

        {status === 'ios-instructions' && (
          <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-3 mb-4">
            <p className="text-blue-300 text-sm text-center">
              📱 <strong>To enable notifications:</strong>
            </p>
            <ol className="text-blue-200 text-xs mt-2 space-y-1 list-decimal list-inside">
              <li>Tap the <strong>Share</strong> button (bottom of screen)</li>
              <li>Scroll and tap <strong>"Add to Home Screen"</strong></li>
              <li>Open the app from your home screen</li>
              <li>Come back here and tap Enable</li>
            </ol>
          </div>
        )}

        {status === 'unsupported' && (
          <div className="bg-orange-500/20 border border-orange-500/30 rounded-lg p-3 mb-4">
            <p className="text-orange-300 text-xs text-center">
              Your browser doesn't support push notifications.
            </p>
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleDismiss}
            className="flex-1 px-4 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg font-medium transition-colors"
          >
            Not Now
          </button>
          <button
            onClick={handleEnable}
            disabled={loading || status === 'success'}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {loading ? 'Enabling...' : status === 'success' ? 'Enabled!' : 'Enable'}
          </button>
        </div>

        {/* Privacy note */}
        <p className="text-white/40 text-xs text-center mt-4">
          You can change this anytime in settings.
        </p>
      </div>
    </div>
  )
}
